/**
 * Secure logging utility that prevents sensitive data exposure
 * Replace console.log/error with these methods for production safety
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enableDebug: boolean;
  enableInfo: boolean;
  enableWarn: boolean;
  enableError: boolean;
}

// Configure based on environment
const config: LoggerConfig = {
  enableDebug: process.env.NODE_ENV !== 'production',
  enableInfo: true,
  enableWarn: true,
  enableError: true
};

// Sensitive patterns to redact
const SENSITIVE_PATTERNS = [
  /token/i,
  /password/i,
  /secret/i,
  /key/i,
  /authorization/i,
  /bearer/i,
  /jwt/i,
  /auth/i,
  /cookie/i,
  /session/i
];

/**
 * Sanitizes log messages to prevent sensitive data exposure
 */
function sanitizeMessage(message: unknown): unknown {
  if (typeof message === 'string') {
    // Check if message contains sensitive patterns
    for (const pattern of SENSITIVE_PATTERNS) {
      if (pattern.test(message)) {
        return '[REDACTED - Contains sensitive data]';
      }
    }
    return message;
  }

  if (typeof message === 'object' && message !== null) {
    // Deep clone to avoid modifying original
    const sanitized: Record<string, unknown> = Array.isArray(message)
      ? ([...message] as unknown as Record<string, unknown>)
      : { ...(message as Record<string, unknown>) };

    // Recursively sanitize object properties
    for (const key in sanitized) {
      // Check if key name is sensitive
      for (const pattern of SENSITIVE_PATTERNS) {
        if (pattern.test(key)) {
          sanitized[key] = '[REDACTED]';
          continue;
        }
      }

      // Recursively sanitize nested values
      if (typeof sanitized[key] === 'object') {
        sanitized[key] = sanitizeMessage(sanitized[key]);
      } else if (typeof sanitized[key] === 'string') {
        // Check if value contains sensitive patterns
        for (const pattern of SENSITIVE_PATTERNS) {
          if (pattern.test(sanitized[key] as string)) {
            sanitized[key] = '[REDACTED]';
            break;
          }
        }
      }
    }

    return Array.isArray(message) ? Object.values(sanitized) : sanitized;
  }

  return message;
}

/**
 * Formats log messages with timestamp and level
 */
function formatMessage(level: LogLevel, ...args: unknown[]): string {
  const timestamp = new Date().toISOString();
  const sanitizedArgs = args.map((arg) => sanitizeMessage(arg));
  return `[${timestamp}] [${level.toUpperCase()}] ${sanitizedArgs.join(' ')}`;
}

/**
 * Secure logger that prevents sensitive data exposure
 */
export const logger = {
  debug: (...args: unknown[]) => {
    if (config.enableDebug) {
      console.log(formatMessage('debug', ...args));
    }
  },

  info: (...args: unknown[]) => {
    if (config.enableInfo) {
      console.log(formatMessage('info', ...args));
    }
  },

  warn: (...args: unknown[]) => {
    if (config.enableWarn) {
      console.warn(formatMessage('warn', ...args));
    }
  },

  error: (...args: unknown[]) => {
    if (config.enableError) {
      console.error(formatMessage('error', ...args));
    }
  }
};

/**
 * Development-only logger for debugging
 * NEVER use in production code
 */
export const devLogger = {
  log: (...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[DEV]', ...args);
    }
  },

  warn: (...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[DEV]', ...args);
    }
  },

  error: (...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[DEV]', ...args);
    }
  }
};
